import {
  Annotation,
  AnnotationStatus,
  EnvironmentType,
  IssueType,
  Metadata,
  PriorityType,
  Transaction,
  TransactionRequest,
  QuestionType,
  ServiceType,
  TransactionCriteria
} from '@jibo-cloud/qas-client'
import { getAuth } from '../servicesClient'
import ChatManager, { ChatData, MessageType } from './ChatManager'
import Settings, { SettingsData } from './Settings'
import {
  getMetadataService,
  processAnnotationService,
  processTransactionService,
  searchAnnotationService,
  searchTransactionService
} from '../services/qas'

export interface InputData {
  clientId: string;
  appName: string;
  userId: string;
  sessionId: string;
  environment: EnvironmentType;
  type: QuestionType | undefined;
  top: number;
  scoreThreshold: number;
  category: string;
  question: string;
  inputData: string;
}

export type TransactionData = TransactionCriteria;
export type AnnotationData = Annotation;

export type SearchResultsData = {
  database: string;
  result: TransactionData[] | AnnotationData[];
};

export type UserData = {
  loggedInUserId: string;
  loggedInUserEmail: string;
  users: any[];
};

export const DefaultUserData = {
  loggedInUserId: 'anonymous',
  loggedInUserEmail: 'anonymous@anonymous.com',
  users: []
}

export default class Model {
  // private _config: any;
  private _settings: Settings;

  public inputClientId = '';
  public inputAppName = '';
  public inputSessionId = '';
  public inputEnvironment: EnvironmentType | undefined;
  public inputType: QuestionType | undefined;
  public inputTop = 1; // NBest
  public inputScoreThreshold = 0;
  public inputCategory = '';
  public inputInputData: string;
  public inputQuestion = '';

  public activeSessionId: string;
  public activeTransactionId: string;
  public activeAnnotationId: string;

  private _annotationData: AnnotationData;
  private _metadataArray: Metadata[];
  private _defaultAppName: string;
  private _loggedInUserId: string;

  constructor () {
    this._settings = new Settings()
    this.resetInputData()
    this.activeSessionId = ''
    this.activeTransactionId = ''
    this.activeAnnotationId = ''
    this._annotationData = this.resetAnnotationData()
    this._metadataArray = []
    this._defaultAppName = ''
    this._loggedInUserId = ''
    ChatManager.Instance() // init
  }

  updateSettings (data: SettingsData): void {
    this._settings.init(data)
    this._settings.saveToLocalStorage()
  }

  get settingsData (): SettingsData {
    return this._settings.json
  }

  get inputData (): InputData {
    return {
      clientId: this.inputClientId,
      appName: this.inputAppName,
      userId: this._loggedInUserId,
      sessionId: this.inputSessionId,
      environment: this.inputEnvironment as EnvironmentType,
      type: this.inputType,
      top: this.inputTop,
      scoreThreshold: this.inputScoreThreshold,
      category: this.inputCategory,
      question: this.inputQuestion,
      inputData: this.inputInputData
    }
  }

  get annotationData (): AnnotationData {
    return this._annotationData
  }

  get chatData (): ChatData {
    return ChatManager.Instance().chatData
  }

  resetInputData () {
    this.inputClientId = 'qas-test-tool'
    this.inputAppName = this.getDefaultAppName()
    this.inputSessionId = ''
    this.inputEnvironment = EnvironmentType.Production
    this.inputType = QuestionType.Qa
    this.inputTop = 1
    this.inputScoreThreshold = 0
    this.inputCategory = ''
    this.inputQuestion = 'what is hepatitis c?'
  }

  public getEmptyAnnotationData (sessionId = '', transactionId = '', intentId = '', appName = ''): Annotation {
    return {
      id: '',
      type: QuestionType.Qa,
      clientId: 'qas-test-tool',
      appName: appName,
      userId: '',
      sessionId: sessionId,
      transactionId: transactionId,
      status: AnnotationStatus.Open,
      issueType: IssueType.WrongAnswer,
      priority: PriorityType.Low,
      assignedTo: 'unassigned',
      revision: 0,
      intentId: intentId,
      category: '',
      deidentifiedInput: '',
      notes: '',
      appSpecificData: {}
    }
  }

  resetAnnotationData (sessionId = '', transactionId = '', intentId = '', appName = ''): AnnotationData {
    this._annotationData = this.getEmptyAnnotationData(sessionId, transactionId, intentId, appName)
    return this._annotationData
  }

  clearSessionId () {
    this.inputSessionId = ''
  }

  /// / QUESTION / TRANSACTION

  submitInputData (data: InputData): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      if (data && data.question) {
        const userData = await this.getUserData()
        if (userData.loggedInUserId) {
          data.userId = userData.loggedInUserId
        }
        this.inputClientId = data.clientId
        this.inputAppName = data.appName
        this.inputSessionId = data.sessionId
        this.inputEnvironment = data.environment
        this.inputType = data.type
        this.inputTop = data.top
        this.inputScoreThreshold = data.scoreThreshold
        this.inputCategory = data.category
        this.inputQuestion = data.question
        this.inputInputData = data.inputData
        try {
          const response = await this.makeTransactionRequest()
          resolve(response)
        } catch (error) {
          reject(error)
        }
      }
    })
  }

  makeTransactionRequest (): Promise<Transaction> {
    if (this.inputQuestion) {
      const isTest = this.inputEnvironment === 'test' // use live environment vs. test

      let extendedInputData = {}
      try {
        extendedInputData = { ...JSON.parse(this.inputInputData) }
      } catch (e) {
        console.log('No extendedInputData found. Ignoring...')
      }

      const data: TransactionRequest = {
        clientId: this.inputClientId,
        appName: this.inputAppName,
        userId: this._loggedInUserId,
        sessionId: this.inputSessionId,
        environment: this.inputEnvironment,
        type: this.inputType,
        input: this.inputQuestion,
        inputData: {
          question: this.inputQuestion,
          top: this.inputTop,
          isTest: isTest,
          scoreThreshold: this.inputScoreThreshold,
          topic: this.inputCategory,
          userId: this._loggedInUserId,
          ...extendedInputData
        }
      }

      return new Promise<Transaction>(async (resolve, reject) => {
        try {
          const question = await processTransactionService(Model.stripEmptyProperties(data))
          if (question) {
            this.inputSessionId = question.sessionId || this.inputSessionId
            this.activeSessionId = question.sessionId || ''
            this.resetAnnotationData(question.sessionId, question.id, question.intentId, question.appName)
            ChatManager.Instance().addMessage(this.inputQuestion, question.id || '', MessageType.userInput)
            if (question.intentDetail) {
              ChatManager.Instance().addMessage(
                question.intentDetail,
                question.id || '',
                MessageType.canonicalQuestion
              )
            }
            let answer = ''
            if (question && question.response && question.response.answers) {
              answer = question.response.answers[0].answer || ''
            }
            if (answer) {
              ChatManager.Instance().addMessage(answer, question.id || '', MessageType.answer)
            }
          }
          resolve(question)
        } catch (e) {
          console.log(e)
          reject(new Error('QAS api call failed: makeTransactionRequest'))
        }
      })
    } else {
      return Promise.reject(new Error('Invalid input data'))
    }
  }

  /// / ANNOTATION

  submitAnnotationData (data: Annotation): Promise<Annotation> {
    return new Promise<Annotation>(async (resolve, reject) => {
      try {
        const result = await processAnnotationService(data)
        this._annotationData = result
        this.activeAnnotationId = result.id || ''
        resolve(result)
      } catch (e) {
        console.log(e)
        reject(new Error('QAS api call failed: submitAnnotationData: received invalid annotation data.'))
      }
    })
  }

  /// / SEARCH

  submitSearchData (data: any): Promise<SearchResultsData> {
    const { database, ...params } = data
    const criteria: any = Model.stripEmptyProperties(params, ['status', 'uiMinimized'])
    if (database === 'annotations' && data.status) {
      const statusList: string[] = []
      const keys: string[] = Object.keys(data.status)
      keys.forEach((key: string) => {
        if (data.status[key]) {
          statusList.push(key)
        }
      })
      criteria.status = statusList
    }
    return this.makeSearchRequest(database, criteria)
  }

  makeSearchRequest (database: string, criteria: any): Promise<SearchResultsData> {
    return new Promise<SearchResultsData>(async (resolve, reject) => {
      try {
        let result: any
        if (database === 'transactions') {
          result = await searchTransactionService(criteria)
        } else if (database === 'annotations') {
          result = await searchAnnotationService(criteria)
        }
        const results: SearchResultsData = {
          result,
          database
        }
        resolve(results)
      } catch (e) {
        console.log(e)
        console.log(`QAS api call failed: makeSearchRequest: error: ${e}`)
        reject(e)
      }
    })
  }

  /// / METADATA

  makeMetadataRequest (): Promise<Metadata[]> {
    // TODO does not seem to be working
    return new Promise<Metadata[]>(async (resolve, reject) => {
      try {
        const result = await getMetadataService()
        this.processMetadataResult(result || [])
        resolve(result)
      } catch (e) {
        reject(new Error('QAS api call failed: makeMetadataRequest: invalid response status.'))
      }
    })
  }

  processMetadataResult (metadataResult: Metadata[]) {
    this._metadataArray = metadataResult
    if (this._metadataArray) {
      this._metadataArray.forEach((metadataData: Metadata) => {
        if (metadataData.isDefault) {
          this._defaultAppName = metadataData.appName
        }
      })
      this.resetInputData()
    }
  }

  getDefaultAppName (): string {
    return this._defaultAppName
  }

  getAppNames (): string[] {
    let result: string[] = []
    if (this._metadataArray) {
      result = this._metadataArray.map(m => m.appName)
    }
    return result
  }

  /// /

  static isTransactionData (object: any): object is TransactionData {
    return 'category' in object
  }

  static isAnnotationData (object: any): object is AnnotationData {
    return 'status' in object
  }

  static stripEmptyProperties (data: any, exclude: string[] = [], include: string[] = []): any {
    const result: any = {} // Object.assign({}, data);
    if (data) {
      const keys: string[] = Object.keys(data)
      keys.forEach((key: string) => {
        if (include.indexOf(key) >= 0 || (data[key] !== '' && data[key] !== undefined && exclude.indexOf(key) === -1)) {
          result[key] = data[key]
        }
      })
    }
    return result
  }

  async getUserData (): Promise<UserData> {
    const result = DefaultUserData
    const auth = getAuth()
    if (auth) {
      result.loggedInUserId = auth.entityId
      this._loggedInUserId = auth.entityId
    }
    result.loggedInUserEmail = auth.email || auth.username
    return result
  }
}

export const serviceTypeOptions = {
  [ServiceType.Qnamaker]: 'QNA Maker',
  [ServiceType.Luis]: 'LUIS',
  [ServiceType.Jiboparser]: 'JIBO Parser',
  [ServiceType.Mint]: 'MINT'
}
